"use client";

import { useEffect, useRef, useState } from "react";
import { AccessDeniedNotice } from "@/app/auth/login/_components/access-denied-notice";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { PendingText } from "@/components/ui/pending-text";
import { startMazricaLogin } from "@/lib/auth/client";
import {
  LOGIN_ERROR_CODE,
  type LoginErrorCode,
  MAZRICA_ACCOUNT_SWITCH,
} from "@/lib/auth/login-error-codes";
import {
  buildMazricaLoginCallbackUrls,
  MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PARAM,
} from "@/lib/auth/mazrica-oauth-callback";

const LOGIN_ERROR_CODES = new Set<string>(Object.values(LOGIN_ERROR_CODE));

function parseLoginErrorCode(value: string | null): LoginErrorCode | null {
  return value && LOGIN_ERROR_CODES.has(value)
    ? (value as LoginErrorCode)
    : null;
}

export default function LoginPage() {
  const [oidcLoading, setOidcLoading] = useState(false);
  const [oidcError, setOidcError] = useState("");
  const [loginErrorCode, setLoginErrorCode] = useState<LoginErrorCode | null>(
    null,
  );
  const [accountSwitchRequired, setAccountSwitchRequired] = useState(false);
  const [hasLoadedLoginParams, setHasLoadedLoginParams] = useState(false);
  const hasStartedIframeOidcLogin = useRef(false);

  const getLoginCallbackUrls = () => {
    if (typeof window === "undefined") {
      return { callbackURL: "/" };
    }

    const searchParams = new URLSearchParams(window.location.search);
    return buildMazricaLoginCallbackUrls(
      searchParams.get("returnTo") ?? searchParams.get("callbackUrl"),
      window.location.origin,
      searchParams.get(MAZRICA_SALES_CONNECTION_REQUEST_NONCE_PARAM),
    );
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setLoginErrorCode(parseLoginErrorCode(searchParams.get("error")));
    setAccountSwitchRequired(
      searchParams.get(MAZRICA_ACCOUNT_SWITCH.queryParam) ===
        MAZRICA_ACCOUNT_SWITCH.queryValue,
    );
    setHasLoadedLoginParams(true);
  }, []);

  const handleMazricaLogin = async () => {
    setOidcLoading(true);
    setOidcError("");

    try {
      const { callbackURL, errorCallbackURL } = getLoginCallbackUrls();
      const redirectUrl = await startMazricaLogin(
        callbackURL,
        errorCallbackURL ? { errorCallbackURL } : undefined,
      );
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
    } catch {
      setOidcError("Mazrica ID ログインを開始できませんでした。");
    }

    setOidcLoading(false);
  };

  // Mazrica Sales に iframe 埋め込みされた場合は、ログイン画面を経由せず
  // Mazrica ID の既存ログインセッションで認証を開始する。
  useEffect(() => {
    if (
      !hasLoadedLoginParams ||
      window.self === window.top ||
      loginErrorCode ||
      accountSwitchRequired ||
      hasStartedIframeOidcLogin.current
    ) {
      return;
    }

    hasStartedIframeOidcLogin.current = true;
    void handleMazricaLogin();
  }, [accountSwitchRequired, hasLoadedLoginParams, loginErrorCode]);

  return (
    <main
      aria-labelledby="login-title"
      className="flex min-h-svh items-center justify-center bg-background px-4"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 id="login-title" className="text-2xl font-medium">
            Sozo AI Agent
          </h1>
          <CardDescription>
            {loginErrorCode
              ? "アクセスできない理由をご確認ください"
              : "ログインしてください"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loginErrorCode && <AccessDeniedNotice code={loginErrorCode} />}

          {accountSwitchRequired && (
            <p
              role="status"
              className="rounded-md border bg-muted/50 px-3 py-3 text-sm text-foreground"
            >
              Sozo AI Agentからログアウトしました。
              利用するアカウントに切り替えてから、下のボタンを押してください。
            </p>
          )}

          {!loginErrorCode && (
            <>
              <Button
                onClick={handleMazricaLogin}
                className="w-full cursor-pointer"
                size="lg"
                disabled={oidcLoading}
                aria-busy={oidcLoading}
                aria-describedby={oidcError ? "oidc-login-error" : undefined}
              >
                {oidcLoading ? (
                  <PendingText>ログイン中…</PendingText>
                ) : (
                  "Mazrica ID でログイン"
                )}
              </Button>
              {oidcError && (
                <p
                  id="oidc-login-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {oidcError}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
